/**
 * Der Zeitplan einer Kampagne in Worten.
 *
 * Eine Stelle, weil ihn drei zeigen: die Übersicht, die Kopfzeile der Akte und
 * der Dialog. Die Reihenfolge der Ziffern ist beliebig — `sendWeekdays` kommt
 * so, wie es gespeichert wurde, und '51' meint dasselbe wie '15'.
 */
import type { WaveStartReason } from '@epasu/core';
import type { CampaignRow } from '@/lib/crm-client';

export const WOCHENTAGE: { ziffer: string; kurz: string }[] = [
  { ziffer: '1', kurz: 'Mo' },
  { ziffer: '2', kurz: 'Di' },
  { ziffer: '3', kurz: 'Mi' },
  { ziffer: '4', kurz: 'Do' },
  { ziffer: '5', kurz: 'Fr' },
  { ziffer: '6', kurz: 'Sa' },
  { ziffer: '7', kurz: 'So' },
];

/** 'Mo–Fr', 'täglich' oder die Aufzählung. */
export function tageInWorten(sendWeekdays: string): string {
  const sortiert = [...new Set(sendWeekdays)].sort().join('');
  if (sortiert === '1234567') return 'täglich';
  if (sortiert === '12345') return 'Mo–Fr';
  const namen = WOCHENTAGE.filter((t) => sortiert.includes(t.ziffer)).map((t) => t.kurz);
  return namen.length > 0 ? namen.join(', ') : 'kein Tag';
}

/**
 * Der ganze Zeitplan als Zeile, oder `null` ohne Zeitplan.
 *
 * Das Startdatum steht nur dabei, solange es in der Zukunft liegt: danach ist
 * es Vergangenheit und sagt über den nächsten Versand nichts mehr.
 */
export function zeitplanInWorten(
  kampagne: Pick<CampaignRow, 'sendAtTime' | 'sendStartDate' | 'sendWeekdays'>,
  heute: string = new Date().toISOString().slice(0, 10),
): string | null {
  if (!kampagne.sendAtTime) return null;
  const teile = [`${kampagne.sendAtTime} Uhr`, tageInWorten(kampagne.sendWeekdays)];
  if (kampagne.sendStartDate && kampagne.sendStartDate > heute) {
    teile.push(`ab ${kampagne.sendStartDate.split('-').reverse().join('.')}`);
  }
  return teile.join(' · ');
}

/**
 * Wie lange ein Versand von `anzahl` Mails mit dieser Pause ungefähr dauert.
 *
 * Gerechnet werden nur die Pausen dazwischen — die SMTP-Aufrufe selbst
 * dauern je eine Sekunde oder weniger und fallen daneben nicht ins Gewicht.
 */
export function versanddauer(anzahl: number, pauseSec: { min: number; max: number }): string {
  const pausen = Math.max(0, anzahl - 1);
  const von = Math.round((pausen * pauseSec.min) / 60);
  const bis = Math.round((pausen * pauseSec.max) / 60);
  if (bis < 1) return 'unter einer Minute';
  if (von === bis) return `ca. ${bis} ${bis === 1 ? 'Minute' : 'Minuten'}`;
  if (von < 1) return `bis zu ${bis} Minuten`;
  return `ca. ${von}–${bis} Minuten`;
}

/** Warum ein Versand nicht gestartet wurde, in Worten. */
export function startAbgewiesen(reason: WaveStartReason): string {
  switch (reason) {
    case 'laeuft_schon':
      return 'Für diese Kampagne läuft gerade schon ein Versand — erst wenn er durch ist, geht der nächste.';
    case 'nicht_aktiv':
      return 'Diese Kampagne ist nicht aktiv — erst auf „Aktiv“ stellen.';
    case 'kein_schritt':
      return 'Diese Kampagne hat noch keinen Schritt.';
    case 'keine_vorlage':
      return 'Der Erstansprache fehlt die Vorlage.';
  }
}
